import { ArrowBack, CheckRounded, InfoOutlined } from '@material-ui/icons';
import {
  TURNSTILE_TOKEN_FIELD,
  TurnstileWidget,
  useTurnstile,
} from '@orientaction/components/Turnstile';
import { publicIconPath } from '@orientaction/utils';
import { FC, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ReactComponent as SmsIcon } from '../../../../Asset/svg/sms.svg';
import { apiServiceNative } from '../../../../utils/fetchNative';
import { getSmsApiErrorMessage } from '../../../../utils/smsApiError';
import RedesignImage from '../../shared/components/RedesignImage';
import { useAuthStyles } from '../Auth.styles';
import HelpModal from '../components/HelpModal';
import { useAccountConfirmContentStyles } from './AccountConfirmContent.styles';

interface LocationState {
  email?: string;
  phone?: string;
}

const AccountConfirmContent: FC = () => {
  const shared = useAuthStyles();
  const local = useAccountConfirmContentStyles();
  const cls = { ...shared, ...local };
  const history = useHistory();
  const location = useLocation<LocationState>();
  const { email, phone } = location.state ?? {};
  const turnstile = useTurnstile();

  const [helpOpen, setHelpOpen] = useState(false);
  const [smsError, setSmsError] = useState('');

  useEffect(() => {
    if (!email) {
      history.replace('/signup');
    }
  }, [email, history]);

  const handleRequestSms = async () => {
    if (!phone) return;
    const turnstileToken = turnstile.getTokenOrError();
    if (!turnstileToken) return;

    setSmsError('');
    try {
      const result = await apiServiceNative.post('/api/users-permissions/user/send-sms-code', {
        phone,
        [TURNSTILE_TOKEN_FIELD]: turnstileToken,
      });
      history.push({
        pathname: '/verify-phone',
        state: { phone, retryAfterSeconds: result?.retryAfterSeconds },
      });
    } catch (error) {
      turnstile.reset();
      setSmsError(getSmsApiErrorMessage(error));
    }
  };

  if (!email) {
    return null;
  }

  return (
    <>
      <div className={cls.statusContainer}>
        <div className={cls.checkCircle}>
          <CheckRounded className={cls.checkIcon} />
        </div>

        <h2 className={cls.statusTitle} data-cy="confirmation-account">
          Confirmation
        </h2>

        <p className={cls.statusDescription}>
          Vous venez de recevoir un email sur votre adresse{' '}
          <span className={cls.semibold}>{email}</span> pour activer votre compte. Veuillez suivre
          les instructions.
        </p>

        <div className={cls.form}>
          <div className={cls.infoBox}>
            <InfoOutlined className={cls.infoIcon} />
            <div className={cls.infoContent}>
              <span className={cls.infoTitle}>Vérifiez vos spams</span>
              <p className={cls.infoText}>
                Vous venez de recevoir un email pour activer votre compte. Veuillez suivre les
                instructions.
              </p>
            </div>
          </div>

          <div className={cls.horizontalRule} />

          <p className={cls.noEmailText}>
            Vous n&apos;avez pas reçu l&apos;email pour valider votre compte ?
          </p>

          {phone && (
            <>
              <TurnstileWidget {...turnstile.widgetProps} error={turnstile.error} />
              <button type="button" className={cls.outlineButton} onClick={handleRequestSms}>
                <SmsIcon style={{ width: 16, height: 16 }} />
                <span>Recevoir un SMS</span>
              </button>
              {smsError && <p className={cls.errorTextMd}>{smsError}</p>}
            </>
          )}

          <button type="button" className={cls.backButton} onClick={() => history.push('/signup')}>
            <ArrowBack className={cls.backIcon} />
            <span>Retour vers l&apos;inscription</span>
          </button>

          <button type="button" className={cls.outlineButton} onClick={() => setHelpOpen(true)}>
            <RedesignImage src={`${publicIconPath}/Info.svg`} alt="" width={16} height={16} />
            <span className={cls.outlineButtonTextPrimary}>Besoin d&apos;aide ?</span>
          </button>
        </div>
      </div>

      <HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
};

export default AccountConfirmContent;
